/**
 * Currency Service with Caching
 * 
 * Provides currency information with local caching to minimize platform API calls.
 * Fetches currency details from the platform when not in cache, with fallback to defaults.
 */

import { roundMonetaryValue, formatMonetaryValue } from '@vaiu/utils/currency';
import { formatMmMoneyDisplayTicker, isMmMoneyCurrency } from '@vaiu/utils/mmMoney';

export interface CurrencyDetails {
  code: string;
  symbol: string;
  decimals: number;
}

class CurrencyService {
  private cache: Map<string, CurrencyDetails> = new Map();
  private cacheExpiryTime: number = 60 * 60 * 1000; // 1 hour in milliseconds
  private cacheTimestamps: Map<string, number> = new Map();
  private pendingRequests: Map<string, Promise<CurrencyDetails>> = new Map();

  constructor() {
    // Load cached data from localStorage on initialization
    this.loadCacheFromStorage();
  }

  /**
   * Get currency details with caching
   * @param code Currency code (e.g., 'USD', 'EUR')
   * @returns Promise<CurrencyDetails>
   */
  async getCurrencyDetails(code: string): Promise<CurrencyDetails> {
    if (!code || typeof code !== 'string') {
      console.warn('🏦 Invalid currency code provided:', code);
      return this.getDefaultCurrencyDetails('USD');
    }

    /** MM$ + issuer id — client-side metadata only; do not call fiat currency APIs. */
    if (isMmMoneyCurrency(code)) {
      const mm: CurrencyDetails = {
        code: code.toLowerCase(),
        symbol: `${formatMmMoneyDisplayTicker()} `,
        decimals: 0,
      };
      return mm;
    }

    const upperCode = code.toUpperCase();
    
    // Check if we have a valid cached entry
    if (this.isCacheValid(upperCode)) {
      console.log(`🏦 Using cached currency details for ${upperCode}`);
      return this.cache.get(upperCode)!;
    }

    // Check if there's already a pending request for this currency
    if (this.pendingRequests.has(upperCode)) {
      console.log(`🏦 Waiting for pending currency request for ${upperCode}`);
      return await this.pendingRequests.get(upperCode)!;
    }

    // Create new request and cache it
    const requestPromise = this.fetchCurrencyFromPlatform(upperCode);
    this.pendingRequests.set(upperCode, requestPromise);

    try {
      const result = await requestPromise;
      
      // Cache the result
      this.setCacheEntry(upperCode, result);
      
      // Clean up pending request
      this.pendingRequests.delete(upperCode);
      
      return result;
    } catch (error) {
      // Only log real errors, not expected missing endpoints
      if (error instanceof Error && !error.message.includes('endpoint returned HTML')) {
        console.error(`🏦 Failed to fetch currency details for ${upperCode}:`, error);
      }
      
      // Clean up pending request
      this.pendingRequests.delete(upperCode);
      
      // Return default currency details if platform call fails
      const defaultDetails = this.getDefaultCurrencyDetails(upperCode);
      
      // Cache the default for a shorter time to retry sooner
      this.setCacheEntry(upperCode, defaultDetails, 5 * 60 * 1000); // 5 minutes
      
      return defaultDetails;
    }
  }

  /**
   * Build POST body for `POST /api/v1/currency/details` (API Reference Manual v2.2.5).
   */
  private buildCurrencyDetailsRequestBody(key: string): Record<string, string> {
    const k = key.trim();

    /** MeMoney wire keys: POST /currency/details uses `{ currencyId: "MM$<issuerUserId>" }`. */
    if (isMmMoneyCurrency(k)) {
      return { currencyId: k };
    }

    if (/^[a-f0-9]{24}$/i.test(k)) {
      return { currencyId: k };
    }
    if (/^MM:/i.test(k)) {
      return { currencyId: k };
    }
    return { code: k.toUpperCase() };
  }

  /** Normalize fiat or Brand payloads into CurrencyDetails. */
  private normalizeCurrencyDetailsPayload(raw: unknown, lookupKey: string): CurrencyDetails {
    let o = raw as Record<string, unknown>;
    if (o?.data && typeof o.data === 'object') o = o.data as Record<string, unknown>;
    if (o?.currency && typeof o.currency === 'object') o = o.currency as Record<string, unknown>;

    const codeRaw = typeof o.code === 'string' && o.code.length > 0 ? o.code : lookupKey;
    const code = codeRaw.toLowerCase();

    let symbol: string;
    if (typeof o.symbol === 'string' && o.symbol.length > 0) {
      symbol = o.symbol;
    } else if (typeof o.name === 'string' && o.name.length > 0) {
      symbol = o.name;
    } else {
      symbol = lookupKey.length <= 8 ? lookupKey : `${lookupKey.slice(0, 8)}…`;
    }

    let decimals =
      typeof o.decimals === 'number' && Number.isFinite(o.decimals) ? o.decimals : 2;

    if (isMmMoneyCurrency(lookupKey)) {
      decimals = 0;
    }

    return { code, symbol, decimals };
  }

  /**
   * Fetch currency details from platform API (POST /currency/details per manual; GET fallback).
   */
  private async fetchCurrencyFromPlatform(code: string): Promise<CurrencyDetails> {
    console.log(`🏦 Fetching currency details from platform for ${code}`);

    try {
      const body = this.buildCurrencyDetailsRequestBody(code);
      const authHeaders: Record<string, string> = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      };
      const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
      if (token) {
        authHeaders.Authorization = `Bearer ${token}`;
      }

      let response = await fetch('/api/v1/currency/details', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(body),
      });

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('text/html')) {
        console.log(`🏦 Currency details endpoint not available for ${code}, using defaults`);
        throw new Error('Currency endpoint returned HTML - likely not implemented');
      }

      if (!response.ok && (response.status === 404 || response.status === 405)) {
        const getHeaders: Record<string, string> = { Accept: 'application/json' };
        if (token) getHeaders.Authorization = `Bearer ${token}`;
        response = await fetch(`/api/v1/currency/details?code=${encodeURIComponent(code)}`, {
          method: 'GET',
          headers: getHeaders,
        });
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const json: unknown = await response.json();
      const currencyDetails = this.normalizeCurrencyDetailsPayload(json, code);

      console.log(`🏦 Successfully fetched currency details for ${code}:`, currencyDetails);
      return currencyDetails;
    } catch (error) {
      if (error instanceof Error && !error.message.includes('endpoint returned HTML')) {
        console.error(`🏦 Platform API call failed for ${code}:`, error);
      }
      throw error;
    }
  }

  /**
   * Get default currency details when platform fails or returns invalid data
   * @param code Currency code
   * @returns CurrencyDetails
   */
  private getDefaultCurrencyDetails(code: string): CurrencyDetails {
    const upperCode = code.toUpperCase();

    // Default currency configurations with proper decimals
    const currencyDefaults: Record<string, CurrencyDetails> = {
      'USD': { code: 'usd', symbol: '$', decimals: 2 },
      'EUR': { code: 'eur', symbol: '€', decimals: 2 },
      'GBP': { code: 'gbp', symbol: '£', decimals: 2 },
      'JPY': { code: 'jpy', symbol: '¥', decimals: 0 },
      'CAD': { code: 'cad', symbol: 'C$', decimals: 2 },
      'AUD': { code: 'aud', symbol: 'A$', decimals: 2 },
      'CHF': { code: 'chf', symbol: 'CHF', decimals: 2 },
      'SEK': { code: 'sek', symbol: 'SEK', decimals: 2 },
      'NOK': { code: 'nok', symbol: 'NOK', decimals: 2 },
      'DKK': { code: 'dkk', symbol: 'DKK', decimals: 2 },
      'BTC': { code: 'btc', symbol: '₿', decimals: 8 },
      'ETH': { code: 'eth', symbol: 'Ξ', decimals: 6 },
      'POINTS': { code: 'points', symbol: 'pts', decimals: 0 },
    };

    return currencyDefaults[upperCode] || {
      code: code.toLowerCase(),
      symbol: '$',
      decimals: 2, // Default to 2 decimals for unknown currencies
    };
  }

  /**
   * Check if cache entry is valid (not expired)
   * @param code Currency code
   * @returns boolean
   */
  private isCacheValid(code: string): boolean {
    if (!this.cache.has(code) || !this.cacheTimestamps.has(code)) {
      return false;
    }

    const cacheTime = this.cacheTimestamps.get(code)!;
    const now = Date.now();
    
    return (now - cacheTime) < this.cacheExpiryTime;
  }

  /**
   * Set cache entry with timestamp
   * @param code Currency code
   * @param details Currency details
   * @param customExpiry Optional custom expiry time in milliseconds
   */
  private setCacheEntry(code: string, details: CurrencyDetails, customExpiry?: number): void {
    this.cache.set(code, details);
    this.cacheTimestamps.set(code, Date.now());
    
    // Save to localStorage for persistence
    this.saveCacheToStorage();
    
    console.log(`🏦 Cached currency details for ${code} (expires in ${customExpiry ? Math.round(customExpiry / 1000 / 60) : Math.round(this.cacheExpiryTime / 1000 / 60)} minutes)`);
  }

  /**
   * Clear expired cache entries
   */
  private cleanExpiredCache(): void {
    const now = Date.now();
    const expiredKeys: string[] = [];

    this.cacheTimestamps.forEach((timestamp, code) => {
      if ((now - timestamp) >= this.cacheExpiryTime) {
        expiredKeys.push(code);
      }
    });

    expiredKeys.forEach(code => {
      this.cache.delete(code);
      this.cacheTimestamps.delete(code);
      console.log(`🏦 Removed expired cache entry for ${code}`);
    });

    if (expiredKeys.length > 0) {
      this.saveCacheToStorage();
    }
  }

  /**
   * Save cache to localStorage for persistence
   */
  private saveCacheToStorage(): void {
    try {
      const cacheData = {
        cache: Array.from(this.cache.entries()),
        timestamps: Array.from(this.cacheTimestamps.entries()),
      };
      localStorage.setItem('currencyCache', JSON.stringify(cacheData));
    } catch (error) {
      console.warn('🏦 Failed to save currency cache to localStorage:', error);
    }
  }

  /**
   * Load cache from localStorage
   */
  private loadCacheFromStorage(): void {
    try {
      const cacheDataString = localStorage.getItem('currencyCache');
      if (!cacheDataString) {
        return;
      }

      const cacheData = JSON.parse(cacheDataString);
      
      // Restore cache and timestamps
      this.cache = new Map(cacheData.cache || []);
      this.cacheTimestamps = new Map(cacheData.timestamps || []);
      
      // Clean expired entries
      this.cleanExpiredCache();
      
      console.log(`🏦 Loaded ${this.cache.size} currency entries from localStorage cache`);
    } catch (error) {
      console.warn('🏦 Failed to load currency cache from localStorage:', error);
      // Clear corrupted cache
      localStorage.removeItem('currencyCache');
    }
  }

  /**
   * Clear all cached currency data
   */
  clearCache(): void {
    console.log('🏦 Clearing all currency cache');
    this.cache.clear();
    this.cacheTimestamps.clear();
    this.pendingRequests.clear();
    localStorage.removeItem('currencyCache');
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats(): {
    totalEntries: number;
    validEntries: number;
    expiredEntries: number;
    pendingRequests: number;
  } {
    const now = Date.now();
    let validEntries = 0;
    let expiredEntries = 0;

    this.cacheTimestamps.forEach((timestamp, code) => {
      if ((now - timestamp) < this.cacheExpiryTime) {
        validEntries++;
      } else {
        expiredEntries++;
      }
    });

    return {
      totalEntries: this.cache.size,
      validEntries,
      expiredEntries,
      pendingRequests: this.pendingRequests.size,
    };
  }

  /**
   * Format currency amount with proper symbol and decimals
   * Rounds the amount first to prevent floating point precision issues (e.g., 1.000003, 8.9999999)
   * @param amount Numeric amount
   * @param currencyCode Currency code
   * @returns Promise<string> Formatted currency string
   */
  async formatCurrency(amount: number, currencyCode: string): Promise<string> {
    const details = await this.getCurrencyDetails(currencyCode);
    // Round first to prevent floating point precision issues
    const roundedAmount = roundMonetaryValue(amount, details.decimals);
    const formattedAmount = formatMonetaryValue(roundedAmount, details.decimals);
    
    console.log(`💰 formatCurrency debug:`, {
      amount,
      currencyCode,
      decimals: details.decimals,
      roundedAmount,
      formattedAmount,
      result: `${details.symbol}${formattedAmount}`
    });
    
    // Simple formatting - you can enhance this with locale-specific formatting
    return `${details.symbol}${formattedAmount}`;
  }

  /**
   * Pre-load commonly used currencies to cache
   * @param currencyCodes Array of currency codes to pre-load
   */
  async preloadCurrencies(currencyCodes: string[]): Promise<void> {
    console.log('🏦 Pre-loading currencies:', currencyCodes);
    
    const promises = currencyCodes.map(code => 
      this.getCurrencyDetails(code).catch(error => {
        console.warn(`🏦 Failed to preload currency ${code}:`, error);
        return null;
      })
    );

    await Promise.all(promises);
    console.log('🏦 Currency pre-loading completed');
  }
}

// Export singleton instance
export const currencyService = new CurrencyService();

// Export the class for testing or custom instances
export { CurrencyService }; 